import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse } from '@/services/utils/handlers';
import { notifyAppError } from '@/services/utils/notifyAppError';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

type ErrorsMap = Record<string, string[]>;

export interface ApiResponseBase<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors?: ErrorsMap;
}

export type PosTicketFooterSource = 'branch' | 'company' | 'fallback';
export type PosTicketFooterPolicyScope = 'branch' | 'company';

export type PosTicketFooterResolved = {
  source: PosTicketFooterSource;
  policy_id?: number | null;
  policy_scope?: PosTicketFooterPolicyScope | null;
  branch_id?: number | null;
  return_policy_lines: string[];
};

export type PosTicketFooterUpsertInput = {
  return_policy_lines: string[];
};

export type PosTicketFooterDeleteBranchResponse = {
  branch_id: number;
  deleted: boolean;
};

export type PosTicketFooterResolvedArgs = {
  branchId?: number | null;
};

const toResolvedQuery = (branchId?: number | null): string => {
  if (!branchId || branchId <= 0) {
    return '';
  }
  const search = new URLSearchParams();
  search.set('branch_id', String(branchId));
  return `?${search.toString()}`;
};

// RTK Query para policies de footer en ticket POS (scope company + branch override).
export const posTicketFooterApi = createApi({
  reducerPath: 'posTicketFooterApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['PosTicketFooterPolicy'],
  endpoints: (builder) => ({
    // GET /v1/pos-ticket-policies/resolved
    getPosTicketFooterResolved: builder.query<
      PosTicketFooterResolved,
      PosTicketFooterResolvedArgs | void
    >({
      query: (args) => ({
        url: `/v1/pos-ticket-policies/resolved${toResolvedQuery(args?.branchId)}`,
        method: 'GET',
      }),
      transformResponse: (resp: ApiResponseBase<PosTicketFooterResolved>) => resp.data,
      transformErrorResponse: handlerErrorResponse('PosTicketFooter.GetResolved'),
      providesTags: (_result, _error, args) => {
        const scopeTag =
          args?.branchId && args.branchId > 0 ? `branch:${args.branchId}` : 'branch:current';
        return [
          { type: 'PosTicketFooterPolicy', id: 'LIST' },
          { type: 'PosTicketFooterPolicy', id: scopeTag },
        ];
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'PosTicketFooter.GetResolved',
            fallbackMessage: 'No se pudo obtener el footer de ticket POS.',
            action: 'pos.ticket.footer.getResolved',
          });
        }
      },
    }),

    // PUT /v1/pos-ticket-policies/company-default
    putPosTicketFooterCompanyDefault: builder.mutation<
      PosTicketFooterResolved,
      PosTicketFooterUpsertInput
    >({
      query: (body) => ({
        url: '/v1/pos-ticket-policies/company-default',
        method: 'PUT',
        body,
      }),
      transformResponse: (resp: ApiResponseBase<PosTicketFooterResolved>) => resp.data,
      transformErrorResponse: handlerErrorResponse('PosTicketFooter.PutCompanyDefault'),
      invalidatesTags: [{ type: 'PosTicketFooterPolicy', id: 'LIST' }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'PosTicketFooter.PutCompanyDefault',
            fallbackMessage: 'No se pudo actualizar el footer por defecto.',
            action: 'pos.ticket.footer.putCompanyDefault',
          });
        }
      },
    }),

    // PUT /v1/pos-ticket-policies/branches/{branch_id}
    putPosTicketFooterBranchOverride: builder.mutation<
      PosTicketFooterResolved,
      { branchId: number; body: PosTicketFooterUpsertInput }
    >({
      query: ({ branchId, body }) => ({
        url: `/v1/pos-ticket-policies/branches/${branchId}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (resp: ApiResponseBase<PosTicketFooterResolved>) => resp.data,
      transformErrorResponse: handlerErrorResponse('PosTicketFooter.PutBranchOverride'),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'PosTicketFooterPolicy', id: 'LIST' },
        { type: 'PosTicketFooterPolicy', id: `branch:${arg.branchId}` },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'PosTicketFooter.PutBranchOverride',
            fallbackMessage: 'No se pudo actualizar el override por sucursal.',
            action: 'pos.ticket.footer.putBranchOverride',
          });
        }
      },
    }),

    // DELETE /v1/pos-ticket-policies/branches/{branch_id}
    deletePosTicketFooterBranchOverride: builder.mutation<
      PosTicketFooterDeleteBranchResponse,
      { branchId: number }
    >({
      query: ({ branchId }) => ({
        url: `/v1/pos-ticket-policies/branches/${branchId}`,
        method: 'DELETE',
      }),
      transformResponse: (resp: ApiResponseBase<PosTicketFooterDeleteBranchResponse>) => resp.data,
      transformErrorResponse: handlerErrorResponse('PosTicketFooter.DeleteBranchOverride'),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'PosTicketFooterPolicy', id: 'LIST' },
        { type: 'PosTicketFooterPolicy', id: `branch:${arg.branchId}` },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'PosTicketFooter.DeleteBranchOverride',
            fallbackMessage: 'No se pudo eliminar el override de la sucursal.',
            action: 'pos.ticket.footer.deleteBranchOverride',
          });
        }
      },
    }),
  }),
});

export const {
  useGetPosTicketFooterResolvedQuery,
  useLazyGetPosTicketFooterResolvedQuery,
  usePutPosTicketFooterCompanyDefaultMutation,
  usePutPosTicketFooterBranchOverrideMutation,
  useDeletePosTicketFooterBranchOverrideMutation,
} = posTicketFooterApi;
